/**
 * Matching tab: tutor/student perspectives, filters, and assignment list.
 */
import * as React from 'react';
import { GraduationCap, User } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTablePagination } from '@/components/common/data-table/DataTablePagination';
import { FilterConfigurePanel } from '@/components/common/data-table/FilterConfigurePanel';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { getRoleLabelPlural } from '@/utils/role-label-utils';
import { useTutorDashboard, useStudentDashboard } from '@/hooks/useMatchingDashboard';
import { useFilterVisibility } from '@/hooks/useFilterVisibility';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useAuthStore } from '@/stores/authStore';
import { usePageActions } from '@/hooks/usePageActions';
import { useSessionFormData } from '@/hooks/useSessionFormData';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  TutorFiltersContent,
  DEFAULT_TUTOR_FILTERS,
  ALL_TUTOR_MATCHING_FILTER_GROUP_IDS,
  TUTOR_FILTER_GROUP_LABELS,
  type TutorFilters,
  type FilterGroupId as TutorFilterGroupId,
} from '@/components/tutors/remote/TutorFilters';
import {
  StudentFiltersContent,
  DEFAULT_STUDENT_FILTERS,
  ALL_STUDENT_MATCHING_FILTER_GROUP_IDS,
  STUDENT_FILTER_GROUP_LABELS,
  type StudentFilters,
  type FilterGroupId as StudentFilterGroupId,
} from '@/components/students/remote/StudentFilters';
import { MatchingTable } from './MatchingTable';
import {
  MATCHING_STATUS_CONFIG,
  MATCHING_STATUS_FILTER_OPTIONS_STUDENT,
  MATCHING_STATUS_FILTER_OPTIONS_TUTOR,
  MATCHING_TAB_FILTER_GROUP_LABELS,
  ASSIGNABILITY_CONFIG,
} from '@/components/pairs/pair-configs';
import { CreateTutorView } from '@/components/tutors/remote/CreateTutorView';
import { CreateStudentView } from '@/components/students/remote/CreateStudentView';
import {
  buildStudentDashboardQueryParams,
  buildTutorDashboardQueryParams,
  exportMatchingDashboardCsv,
  hasActiveMatchingFilters,
  matchingPerspectiveLabels,
  matchingStatusAfterPerspectiveChange,
  matchingTableColSpan,
  pickPerspective,
  toMatchingDashboardRows,
  type MatchingDashboardRow,
  type MatchingPerspective,
} from '../matching-utils';

/** Matching-status chips; tutor and student lists differ (`partially-matched` is tutor-only). */
function MatchingStatusFilterOptions({
  perspective,
  matchingStatus,
  onSelect,
}: Readonly<{
  perspective: MatchingPerspective;
  matchingStatus?: string;
  onSelect: (value: string | undefined) => void;
}>) {
  const options = pickPerspective(
    perspective,
    MATCHING_STATUS_FILTER_OPTIONS_TUTOR,
    MATCHING_STATUS_FILTER_OPTIONS_STUDENT,
  );
  return (
    <div className="filter-options">
      {options.map(({ value, label }) => {
        const config = MATCHING_STATUS_CONFIG[value];
        const IconComponent = config?.icon;
        return (
          <Button
            key={value}
            variant="custom"
            size="sm"
            className={cn(
              'inline-flex items-center gap-1.5',
              'filter-badge-inactive',
              matchingStatus === value && 'filter-badge-active',
            )}
            style={
              config?.badgeColor
                ? ({ '--filter-color': config.badgeColor } as React.CSSProperties)
                : undefined
            }
            onClick={() => onSelect(matchingStatus === value ? undefined : value)}
          >
            {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
            {label}
          </Button>
        );
      })}
    </div>
  );
}

/** Assignable / not-assignable chips; click again to clear. */
function AssignabilityFilterOptions({
  assignability,
  onSelect,
}: Readonly<{
  assignability: boolean | undefined;
  onSelect: (value: boolean | undefined) => void;
}>) {
  return (
    <div className="filter-options">
      {([true, false] as const).map((value) => {
        const config = ASSIGNABILITY_CONFIG[String(value) as 'true' | 'false'];
        const IconComponent = config.icon;
        return (
          <Button
            key={String(value)}
            variant="custom"
            size="sm"
            className={cn(
              'inline-flex items-center gap-1.5',
              assignability === value ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
            onClick={() => onSelect(assignability === value ? undefined : value)}
          >
            {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
            {config.label}
          </Button>
        );
      })}
    </div>
  );
}

/** Perspective-specific tutor or student filter groups (reset via `filtersResetKey`). */
function MatchingProfileFilters({
  perspective,
  filtersResetKey,
  tutorFilters,
  studentFilters,
  tutorVisibleFilterIds,
  studentVisibleFilterIds,
  onTutorFilterChange,
  onStudentFilterChange,
}: Readonly<{
  perspective: MatchingPerspective;
  filtersResetKey: number;
  tutorFilters: TutorFilters;
  studentFilters: StudentFilters;
  tutorVisibleFilterIds: TutorFilterGroupId[];
  studentVisibleFilterIds: StudentFilterGroupId[];
  onTutorFilterChange: (patch: Partial<TutorFilters>) => void;
  onStudentFilterChange: (patch: Partial<StudentFilters>) => void;
}>) {
  if (perspective === 'tutor') {
    return (
      <TutorFiltersContent
        key={`tutor-filters-${filtersResetKey}`}
        filters={tutorFilters}
        visibleFilterIds={tutorVisibleFilterIds}
        onFilterChange={onTutorFilterChange}
      />
    );
  }
  return (
    <StudentFiltersContent
      key={`student-filters-${filtersResetKey}`}
      filters={studentFilters}
      visibleFilterIds={studentVisibleFilterIds}
      onFilterChange={onStudentFilterChange}
      activeTab="students"
    />
  );
}

/** Hidden when the list is empty and no filters are active. */
function MatchingTabPagination({
  show,
  page,
  totalPages,
  totalItems,
  startIndex,
  endIndex,
  itemLabel,
  onPageChange,
}: Readonly<{
  show: boolean;
  page: number;
  totalPages: number;
  totalItems: number;
  startIndex: number;
  endIndex: number;
  itemLabel: string;
  onPageChange: (page: number) => void;
}>) {
  if (!show) return null;
  return (
    <DataTablePagination
      currentPage={page}
      totalPages={totalPages}
      totalItems={totalItems}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel={itemLabel}
      onPageChange={onPageChange}
    />
  );
}

/** Inline API error banner; omitted when `message` is empty. */
function MatchingDashboardError({ error }: Readonly<{ error?: unknown }>) {
  if (!error) return null;
  return (
    <div className="mx-6 my-4 rounded-lg border border-[rgb(var(--color-danger))] bg-[color-mix(in_srgb,rgb(var(--color-danger))_8%,transparent)] p-4 text-center">
      <p className="text-sm text-[rgb(var(--color-danger))]">{getErrorMessage(error)}</p>
    </div>
  );
}

/** Create-tutor or create-student dialog matching the current perspective. */
function MatchingCreateDialog({
  open,
  perspective,
  onOpenChange,
}: Readonly<{
  open: boolean;
  perspective: MatchingPerspective;
  onOpenChange: (open: boolean) => void;
}>) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onOpenChange(false);
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 custom-scroll-area" hideCloseButton>
        {open && perspective === 'tutor' && (
          <CreateTutorView
            onSuccess={() => onOpenChange(false)}
            onClose={() => onOpenChange(false)}
          />
        )}
        {open && perspective === 'student' && (
          <CreateStudentView
            onSuccess={() => onOpenChange(false)}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Remote matching tab: tutor/student perspectives, filters, and assignment list. */
export function ConfigureMatchingView() {
  const [perspective, setPerspective] = React.useState<'tutor' | 'student'>('tutor');

  /** Separate filter states per perspective — user preferences are persisted independently. */
  const [tutorFilters, setTutorFilters] = React.useState<TutorFilters>(DEFAULT_TUTOR_FILTERS);
  const [studentFilters, setStudentFilters] =
    React.useState<StudentFilters>(DEFAULT_STUDENT_FILTERS);
  const [filtersResetKey, setFiltersResetKey] = React.useState(0);
  /** Matching status is common to both perspectives (matched / unmatched). */
  const [matchingStatus, setMatchingStatus] = React.useState<string | undefined>(undefined);
  /** Assignability column filter — undefined = both, true = assignable, false = not assignable. */
  const [assignability, setAssignability] = React.useState<boolean | undefined>(undefined);

  const [isExportingCsv, setIsExportingCsv] = React.useState(false);
  const [showFilters, setShowFilters] = React.useState(false);
  const [showConfigure, setShowConfigure] = React.useState(false);
  const [showCreateForm, setShowCreateForm] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const PAGE_SIZE = 10;

  const { openRowId, openRow, closeRow } = useDetailsView();
  const { user } = useAuthStore();
  const companyId = user?.companyId;
  const { canCreate } = usePageActions('pairs');
  const { programs = [] } = useSessionFormData();

  /**
   * Two independent filter-visibility namespaces (`matching-remote-tutors` /
   * `matching-remote-students`), separate from the management-page prefs.
   * `elementsName` reuses the management-page RBAC config so the same DB entries drive visibility.
   */
  const tutorFv = useFilterVisibility<TutorFilterGroupId>({
    preferencesName: 'matching-remote-tutors',
    elementsName: 'tutors',
    allFilterGroupIds: ALL_TUTOR_MATCHING_FILTER_GROUP_IDS,
  });
  const studentFv = useFilterVisibility<StudentFilterGroupId>({
    preferencesName: 'matching-remote-students',
    elementsName: 'students',
    allFilterGroupIds: ALL_STUDENT_MATCHING_FILTER_GROUP_IDS,
  });

  const currentFv = pickPerspective(perspective, tutorFv, studentFv);
  const currentFilterLabels = pickPerspective(
    perspective,
    TUTOR_FILTER_GROUP_LABELS,
    STUDENT_FILTER_GROUP_LABELS,
  );

  /**
   * Active-filter badge: boolean `false` (e.g. study participation = No) must count as active
   * so Reset stays enabled.
   */
  const currentFilters = pickPerspective(perspective, tutorFilters, studentFilters);
  const hasActiveFilters = hasActiveMatchingFilters(
    matchingStatus,
    assignability,
    currentFilters as Record<string, unknown>,
  );

  /** Switching perspective resets page/row/configure and drops student-invalid status. */
  const handlePerspectiveChange = (next: MatchingPerspective) => {
    setPerspective(next);
    setPage(1);
    closeRow();
    setShowConfigure(false);
    setMatchingStatus(matchingStatusAfterPerspectiveChange(next, matchingStatus));
  };

  /** Clears both perspectives and remounts filter controls via `filtersResetKey`. */
  const handleResetFilters = () => {
    setMatchingStatus(undefined);
    setAssignability(undefined);
    setTutorFilters(DEFAULT_TUTOR_FILTERS);
    setStudentFilters(DEFAULT_STUDENT_FILTERS);
    setFiltersResetKey((k) => k + 1);
    setPage(1);
    closeRow();
  };

  /** Dashboard API params — map from the rich filter states down to what the backend supports. */
  const tutorDashboard = useTutorDashboard(
    perspective === 'tutor'
      ? buildTutorDashboardQueryParams(tutorFilters, {
          page,
          pageSize: PAGE_SIZE,
          matchingStatus,
          assignability,
        })
      : undefined,
  );
  const studentDashboard = useStudentDashboard(
    perspective === 'student'
      ? buildStudentDashboardQueryParams(studentFilters, {
          page,
          pageSize: PAGE_SIZE,
          matchingStatus,
          assignability,
        })
      : undefined,
  );

  const currentDashboard = pickPerspective(perspective, tutorDashboard, studentDashboard);
  /** Dashboard items keyed by `userId` for row expand/collapse. */
  const tableData = React.useMemo<MatchingDashboardRow[]>(
    () => toMatchingDashboardRows(currentDashboard.data?.data),
    [currentDashboard.data],
  );
  const totalItems = currentDashboard.data?.total ?? 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;
  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;

  const labels = matchingPerspectiveLabels(perspective);
  const emptyText = `Aucun ${labels.singular} trouvé`;
  const tableColSpan = matchingTableColSpan(perspective);

  /** Full-dashboard CSV (not just the current page). */
  const handleExportCsv = async () => {
    if (!companyId) return;
    setIsExportingCsv(true);
    try {
      await exportMatchingDashboardCsv({
        companyId,
        perspective,
        matchingStatus,
        assignability,
        tutorFilters,
        studentFilters,
      });
    } finally {
      setIsExportingCsv(false);
    }
  };

  /** Filter groups — matching status first, then the perspective-specific rich filters. */
  const filterGroups = React.useMemo(
    () => [
      {
        id: 'matching-status',
        label: MATCHING_TAB_FILTER_GROUP_LABELS.matchingStatus,
        type: 'custom' as const,
        customContent: (
          <MatchingStatusFilterOptions
            perspective={perspective}
            matchingStatus={matchingStatus}
            onSelect={(value) => {
              setMatchingStatus(value);
              setPage(1);
            }}
          />
        ),
      },
      {
        id: 'assignability',
        label: MATCHING_TAB_FILTER_GROUP_LABELS.assignability,
        type: 'custom' as const,
        customContent: (
          <AssignabilityFilterOptions
            assignability={assignability}
            onSelect={(value) => {
              setAssignability(value);
              setPage(1);
            }}
          />
        ),
      },
      {
        id: 'profile-filters',
        label: '',
        type: 'custom' as const,
        customContent: (
          <MatchingProfileFilters
            perspective={perspective}
            filtersResetKey={filtersResetKey}
            tutorFilters={tutorFilters}
            studentFilters={studentFilters}
            tutorVisibleFilterIds={tutorFv.visibleFilterIds as TutorFilterGroupId[]}
            studentVisibleFilterIds={studentFv.visibleFilterIds as StudentFilterGroupId[]}
            onTutorFilterChange={(patch) => {
              setTutorFilters((prev) => ({ ...prev, ...patch }));
              setPage(1);
            }}
            onStudentFilterChange={(patch) => {
              setStudentFilters((prev) => ({ ...prev, ...patch }));
              setPage(1);
            }}
          />
        ),
      },
    ],
    [
      perspective,
      matchingStatus,
      assignability,
      tutorFilters,
      studentFilters,
      tutorFv.visibleFilterIds,
      studentFv.visibleFilterIds,
      filtersResetKey,
    ],
  );

  return (
    <>
      <section className="table-container">
        <div className="matching-column-header">
          <div className="flex flex-wrap items-center justify-between gap-3 w-full">
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center border shrink-0"
                style={{
                  background: 'color-mix(in srgb, var(--color-1-nuance-1) 10%, transparent)',
                  borderColor: 'var(--color-1-nuance-1)',
                }}
              >
                {pickPerspective(
                  perspective,
                  <GraduationCap
                    className="w-4 h-4"
                    style={{ color: 'var(--color-1-nuance-1)' }}
                  />,
                  <User className="w-4 h-4" style={{ color: 'var(--color-1-nuance-1)' }} />,
                )}
              </div>
              <div className="leading-tight">
                <h3
                  className="text-base font-semibold"
                  style={{ color: 'var(--color-1-nuance-1)' }}
                >
                  {labels.title}
                </h3>
                <p className="text-xs text-muted-foreground leading-tight">
                  {totalItems} {totalItems > 1 ? labels.plural : labels.singular}
                </p>
              </div>

              <div className="ml-6">
                <SegmentedControl
                  size="md"
                  className="h-[42px]"
                  value={perspective}
                  onChange={(value) => handlePerspectiveChange(value as 'tutor' | 'student')}
                  options={[
                    {
                      value: 'tutor',
                      label: getRoleLabelPlural('tutor'),
                      icon: <GraduationCap className="h-4 w-4" />,
                    },
                    {
                      value: 'student',
                      label: getRoleLabelPlural('student'),
                      icon: <User className="h-4 w-4" />,
                    },
                  ]}
                />
              </div>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
              {canCreate && (
                <Button
                  preset="toolbar-create"
                  tooltipText={labels.createTooltip}
                  onClick={() => {
                    closeRow();
                    setShowCreateForm(true);
                  }}
                />
              )}

              <Button
                preset="toolbar-filter"
                className={cn(showFilters && 'section-action-button-active')}
                showBadge={hasActiveFilters}
                onClick={() => {
                  setShowFilters((v) => !v);
                  if (showFilters) setShowConfigure(false);
                }}
              />

              <Button
                preset="toolbar-export"
                onClick={handleExportCsv}
                loading={isExportingCsv}
                disabled={isExportingCsv}
                tooltipText="Exporter les résultats en CSV"
              />
            </div>
          </div>
        </div>

        <DataTableFilters
          show={showFilters}
          hasActiveFilters={hasActiveFilters}
          filterGroups={filterGroups}
          onClearFilters={handleResetFilters}
          resultCount={totalItems}
          resultLabel={labels.resultLabel}
          onToggleConfigure={
            currentFv.allowedFilterIds.length > 0 ? () => setShowConfigure((v) => !v) : undefined
          }
          showConfigure={showConfigure}
          configureContent={
            <FilterConfigurePanel
              allowedFilterIds={currentFv.allowedFilterIds}
              filterVisibility={currentFv.filterVisibility}
              filterGroupLabels={currentFilterLabels}
              onToggle={currentFv.handleToggleFilterVisibility}
              isSaving={currentFv.isSavingFilterVisibility}
            />
          }
        />

        <MatchingTable
          perspective={perspective}
          tableColSpan={tableColSpan}
          isLoading={currentDashboard.isLoading}
          tableData={tableData}
          emptyText={emptyText}
          openRowId={openRowId}
          programs={programs}
          onOpenRow={openRow}
          onCloseRow={closeRow}
        />

        <MatchingTabPagination
          show={totalItems > 0 || hasActiveFilters}
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          itemLabel={labels.plural}
          onPageChange={(nextPage) => {
            setPage(nextPage);
            closeRow();
          }}
        />
      </section>

      <MatchingDashboardError error={currentDashboard.error} />

      <MatchingCreateDialog
        open={showCreateForm}
        perspective={perspective}
        onOpenChange={setShowCreateForm}
      />
    </>
  );
}
